import { Separator } from '@connected/ui/components/separator';
import { Switch } from '@connected/ui/components/switch';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_auth/dashboard/organizations/$organizationId/settings/inspection/')({
    component: RouteComponent,
});

function getStorageKey() {
    return 'inspection_settings_switches';
}

function getInitialSwitchState(items: { title: string }[]): Record<string, boolean> {
    const key = getStorageKey();
    const stored = localStorage.getItem(key);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            // Only keep keys that are in items
            const valid: Record<string, boolean> = {};
            items.forEach(({ title }) => {
                valid[title] = typeof parsed[title] === 'boolean' ? parsed[title] : false;
            });
            return valid;
        }
        catch {
            // fallback to all false
        }
    }
    const initial: Record<string, boolean> = {};
    items.forEach(({ title }) => {
        initial[title] = false;
    });
    return initial;
}

function RouteComponent() {
    const { t } = useTranslation('dashboard');

    const items = useMemo(() => [
        { title: t('dashboard.settings.inspection.inspection-settings.switch.use-offer-module') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.use-inspection-plan-module') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.use-planning-module') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.always-perform-measurements') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.always-measurements-in-report') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.extensive-riso-earth-switch') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.extensive-riso-earth-automats') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.extensive-riso-end-groups') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.switching-authorization-module') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.record-control-cabinets') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.give-recommendations') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.include-classification-group-in-finding') },
        { title: t('dashboard.settings.inspection.inspection-settings.switch.scios-certified') },
    ], [t]);

    const [switchState, setSwitchState] = useState<Record<string, boolean>>(() => getInitialSwitchState(items));

    // Update state if items change (e.g. language changes)
    useEffect(() => {
        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setSwitchState(getInitialSwitchState(items));
    }, [items]);

    // Persist to localStorage on change
    useEffect(() => {
        localStorage.setItem(getStorageKey(), JSON.stringify(switchState));
    }, [switchState]);

    function handleToggle(title: string) {
        setSwitchState(prev => ({
            ...prev,
            [title]: !prev[title],
        }));
    }

    return (
        <>
            <div className="flex flex-row gap-4">
                <div className="w-1/4">
                    <h3 className="text-base text-text-primary/70 font-semibold">
                        {t('dashboard.settings.inspection.inspection-settings.title')}
                    </h3>
                    <p className="text-sm text-text-primary/70">
                        {t('dashboard.settings.inspection.inspection-settings.subtitle')}
                    </p>
                </div>
                <div className="flex flex-col w-3/4 justify-start rounded-2xl gap-4 h-full">
                    {items.map(({ title }) => (
                        <div key={title} className="flex flex-row items-center gap-2">
                            <Switch checked={!!switchState[title]} onCheckedChange={() => handleToggle(title)} />
                            <h3 className="text-base text-text-primary/70 font-semibold">
                                {title}
                            </h3>
                        </div>
                    ))}
                </div>
            </div>
            <Separator className="my-5" />
        </>
    );
}
